import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

const EFFECTIVE_DATE = '24 May 2026';

function PrivacyPage() {
  usePageTitle('Privacy Policy');

  return (
    <div className="bg-brand-cream-shadow min-h-dvh">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-12 sm:py-24">

        <header className="mb-12 border-b border-brand-sage/15 pb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Legal
          </div>
          <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tightest text-brand-ink-soft sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm text-brand-ink-soft/55">
            Effective {EFFECTIVE_DATE}
          </p>
          <p className="mt-4 text-base text-brand-ink-soft/75 leading-relaxed">
            This policy explains what personal information Bookaburra collects,
            why we collect it, how we store and use it, and how you can access
            or correct it. It applies to the Bookaburra website at{' '}
            <a href="https://bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
              bookaburra.com
            </a>{' '}
            and the booking platform at{' '}
            <a href="https://app.bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
              app.bookaburra.com
            </a>
            .
          </p>
        </header>

        <div className="space-y-10">

          <Section id="who-we-are" heading="1. Who we are">
            <P>
              Bookaburra is an online booking platform for service businesses,
              operated by Bookaburra (legal entity registration pending). We are
              bound by the <em>Privacy Act 1988</em> (Cth) and the Australian
              Privacy Principles (APPs).
            </P>
            <P>
              Questions about this policy:{' '}
              <a href="mailto:hello@bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
                hello@bookaburra.com
              </a>
              .
            </P>
          </Section>

          <Section id="two-types" heading="2. Two types of people this policy covers">
            <P>
              Bookaburra sits between two groups:
            </P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>
                <strong>Operators and staff</strong> — businesses and their team
                members who sign up for a Bookaburra account to manage their
                bookings.
              </li>
              <li>
                <strong>Clients</strong> — people who book an appointment through
                a Bookaburra-powered booking page. Clients are customers of the
                operator, not of Bookaburra directly.
              </li>
            </ul>
            <P className="mt-4">
              When clients book through your page, you (the operator) are the
              primary collector of their information. You are responsible for
              your own Privacy Act obligations to your clients, including
              maintaining your own privacy notice. Bookaburra acts as a data
              processor on your behalf.
            </P>
          </Section>

          <Section id="what-we-collect" heading="3. What we collect and why">
            <H3>Operators and staff</H3>
            <Table
              headers={['Information', 'Why we collect it']}
              rows={[
                ['Full name and email address', 'To create and manage your account'],
                ['Password (stored as a one-way cryptographic hash — we cannot read it)', 'Authentication'],
                ['Business name, timezone, and booking page settings', 'To configure and display your booking page correctly'],
                ['Billing name and address', 'Passed to Stripe for invoice generation; we do not store card numbers'],
                ['IP addresses, access times, and error logs', 'Security, debugging, and abuse prevention'],
              ]}
            />

            <H3>Clients (people who book through a Bookaburra-powered page)</H3>
            <Table
              headers={['Information', 'Why we collect it']}
              rows={[
                ['Full name', 'To identify the booking and address notifications correctly'],
                ['Email address', 'To send booking confirmations, reminders, and the magic-link for self-service rescheduling'],
                ['Phone number (if provided)', 'To send SMS confirmations and reminders where the operator has enabled SMS'],
                ['Booking history and notes', 'To let clients and operators manage, reschedule, or cancel bookings'],
              ]}
            />

            <H3>All visitors</H3>
            <Table
              headers={['Information', 'Why we collect it']}
              rows={[
                ['Browser type and device information', 'Aggregate performance monitoring — not linked to individuals'],
                ['Referrer URL', 'Understanding how people find Bookaburra'],
              ]}
            />
          </Section>

          <Section id="what-we-dont-log" heading="4. What we don't log">
            <P>
              Some platform features call AI services on your behalf — the
              chat-based booking assistant, the voice booking agent, and the
              spoken-utterance intent router. We deliberately do not retain
              the content of those interactions.
            </P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>
                We do not log prompt text or response text from any AI call
                (chat, voice, intent router).
              </li>
              <li>
                We do not store voice transcripts. The speech-to-text response
                is converted to a state-machine choice and discarded in
                memory at the end of the turn.
              </li>
              <li>
                API usage metadata (business id, model name, units, timestamp)
                <em> is </em>stored — for billing visibility and abuse
                detection — but never with the content of the request or
                response.
              </li>
            </ul>
          </Section>

          <Section id="how-we-use" heading="5. How we use personal information">
            <P>We use personal information only to:</P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>Provide, operate, and improve the Bookaburra platform</li>
              <li>Send transactional emails and SMS messages (booking confirmations, reminders, reschedule notifications, cancellations)</li>
              <li>Process subscription payments and SMS top-up purchases</li>
              <li>Detect, prevent, and respond to fraud, spam, and abuse</li>
              <li>Comply with legal obligations and respond to lawful requests</li>
            </ul>
            <P className="mt-4">
              We do not sell personal information to third parties. We do not use
              client booking data for advertising purposes.
            </P>
          </Section>

          <Section id="who-we-share-with" heading="6. Who we share information with">
            <P>
              We use a small number of third-party providers to operate the
              platform. We share only what each provider needs to do their job.
            </P>
            <Table
              headers={['Provider', 'Purpose', 'Data shared']}
              rows={[
                ['Cloudflare', 'Hosting, database, email delivery, DDoS mitigation', 'All platform data — stored in Australia'],
                ['Stripe', 'Subscription billing and payment processing', 'Operator billing name and email only; Stripe handles card data directly'],
                ['Twilio', 'SMS delivery', 'Client phone number and message text'],
                ['Resend', 'Transactional email delivery', 'Recipient email address and message content'],
                ['Cloudflare Workers AI', 'AI booking assistant (chat and voice)', 'Booking-related session context only; not used to train models'],
              ]}
            />
            <P className="mt-4">
              We do not sell, rent, or trade personal information with any other
              third parties. We may disclose information if required to do so by
              law or valid legal process.
            </P>
          </Section>

          <Section id="where-data-is-stored" heading="7. Where your data is stored">
            <P>
              All platform data is stored in Australia. Cloudflare operates the
              database in the Oceania region. Some providers (Stripe, Twilio,
              Resend) may process data outside Australia as part of delivering
              their services; these providers maintain appropriate safeguards.
            </P>
          </Section>

          <Section id="retention" heading="8. How long we keep it">
            <Table
              headers={['Data', 'Retention period']}
              rows={[
                ['Active account data', 'For as long as your account remains open'],
                ['Booking records', '7 years from the booking date (Australian tax record-keeping requirements)'],
                ['Email and SMS delivery logs', '90 days'],
                ['Account data after deletion', 'Purged within 30 days of account closure or deletion request'],
              ]}
            />
          </Section>

          <Section id="your-rights" heading="9. Your rights">
            <P>
              Under the <em>Privacy Act 1988</em> and the Australian Privacy
              Principles, you have the right to:
            </P>
            <ul className="mt-3 list-disc list-outside pl-5 space-y-2 text-base text-brand-ink-soft/80 leading-relaxed">
              <li>
                <strong>Access</strong> the personal information we hold about you
              </li>
              <li>
                <strong>Correct</strong> inaccurate or out-of-date information
              </li>
              <li>
                <strong>Request deletion</strong> of your account and associated data
              </li>
              <li>
                <strong>Lodge a complaint</strong> with the Office of the Australian
                Information Commissioner (OAIC) at{' '}
                <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-brand-sage hover:underline underline-offset-2">
                  oaic.gov.au
                </a>{' '}
                if you believe we have mishandled your information
              </li>
            </ul>
            <P className="mt-4">
              To exercise any of these rights, email{' '}
              <a href="mailto:hello@bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
                hello@bookaburra.com
              </a>
              . We will respond within 30 days.
            </P>
            <P>
              If you are a client of a business that uses Bookaburra, please
              contact that business directly first — they control your booking
              data and are responsible for responding to your access and
              correction requests.
            </P>
          </Section>

          <Section id="security" heading="10. Security">
            <P>
              We protect personal information using encrypted connections (HTTPS),
              one-way password hashing, access controls, and Cloudflare&rsquo;s
              bot mitigation on all public forms.
            </P>
            <P>
              No system is completely immune to attack. If we discover a data
              breach that is likely to cause serious harm, we will notify affected
              individuals and the OAIC as required under the Notifiable Data
              Breaches (NDB) scheme.
            </P>
          </Section>

          <Section id="cookies" heading="11. Cookies and local storage">
            <P>
              We use session cookies for authentication (so you stay logged in)
              and browser local storage for UI preferences (such as your selected
              language). We do not use third-party advertising or tracking cookies.
            </P>
          </Section>

          <Section id="children" heading="12. Children">
            <P>
              Bookaburra is not directed at children under 16. We do not
              knowingly collect personal information from children. If you believe
              a child has created an account or provided personal information
              without appropriate consent, please contact us at{' '}
              <a href="mailto:hello@bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
                hello@bookaburra.com
              </a>{' '}
              and we will delete it promptly.
            </P>
          </Section>

          <Section id="changes" heading="13. Changes to this policy">
            <P>
              We may update this policy from time to time. We will post the
              updated version here with a new &ldquo;Effective&rdquo; date. For
              material changes, we will notify operators by email at least 14 days
              before the change takes effect. Continued use of Bookaburra after
              that date constitutes acceptance.
            </P>
          </Section>

          <Section id="contact" heading="14. Contact">
            <P>
              Privacy enquiries:{' '}
              <a href="mailto:hello@bookaburra.com" className="text-brand-sage hover:underline underline-offset-2">
                hello@bookaburra.com
              </a>
            </P>
            <P>
              If you are not satisfied with our response, you may escalate to the
              OAIC:{' '}
              <a href="https://www.oaic.gov.au/privacy/privacy-complaints" target="_blank" rel="noopener noreferrer" className="text-brand-sage hover:underline underline-offset-2">
                oaic.gov.au/privacy/privacy-complaints
              </a>
            </P>
          </Section>

        </div>

        <footer className="mt-16 border-t border-brand-sage/15 pt-8 text-sm text-brand-ink-soft/50">
          <p>
            <a href="/legal" className="hover:text-brand-sage underline-offset-2 hover:underline">
              &larr; Back to Legal
            </a>
          </p>
        </footer>

      </div>
    </div>
  );
}

function Section({ id, heading, children }: { id: string; heading: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="text-xl font-bold text-brand-ink-soft mb-4">{heading}</h2>
      {children}
    </section>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-base font-semibold text-brand-ink-soft mt-6 mb-2">{children}</h3>
  );
}

function P({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base text-brand-ink-soft/80 leading-relaxed mb-3 ${className ?? ''}`}>
      {children}
    </p>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto mt-3 mb-4">
      <table className="w-full text-sm border-collapse rounded-xl overflow-hidden">
        <thead>
          <tr className="bg-brand-cream text-left">
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 font-semibold text-brand-ink-soft/70 border-b border-brand-sage/15">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-brand-cream/50">
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-brand-sage/10 last:border-0">
              {row.map((cell, j) => (
                <td key={j} className={`px-4 py-3 text-brand-ink-soft/75 align-top ${j === 0 ? 'font-medium text-brand-ink-soft/90' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
